import { type FC, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { DefinitionTooltip } from "src/components/common";
import {
  normalizeTooltipValue,
  OrganicChartTooltip,
} from "src/dashboards/paidMedia/components/metaAds/metaChartConfig";
import {
  TIME_SERIES_METRICS,
  TREND_PAGE_SIZE,
  type EngagementTrendGranularity,
  type TimeSeriesDatum,
  type TimeSeriesMetricKey,
} from "../../data/engagementOverviewTypes";
import {
  formatAxisCompact,
  formatPercent,
  formatShortDate,
  formatTooltipCount,
  formatTooltipDate,
  formatTooltipPercent,
} from "../../utils/engagementOverviewFormat";
import ChartLegendChips from "./ChartLegendChips";
import EngagementEmptyState from "./EngagementEmptyState";
import EngagementSectionPagination from "./EngagementSectionPagination";
import { getEngagementTooltipInfo } from "./getEngagementTooltipInfo";

export interface TimeSeriesAnalysisProps {
  dailyData: TimeSeriesDatum[];
  monthlyData: TimeSeriesDatum[];
  sectionTitle?: string;
  tooltipKey?: string;
}

const TimeSeriesAnalysis: FC<TimeSeriesAnalysisProps> = ({
  dailyData,
  monthlyData,
  sectionTitle,
  tooltipKey = "timeSeriesAnalysis",
}) => {
  const { t, i18n } = useTranslation();
  const resolvedSectionTitle = sectionTitle ?? t("engagementOverview.shared.timeSeriesSectionTitle");
  const [granularity, setGranularity] = useState<EngagementTrendGranularity>("daily");
  const [page, setPage] = useState(1);
  const [activeMetrics, setActiveMetrics] = useState<TimeSeriesMetricKey[]>(
    TIME_SERIES_METRICS.map(metric => metric.key),
  );

  const legendItems = useMemo(
    () =>
      TIME_SERIES_METRICS.map(metric => ({
        key: metric.key,
        label: t(metric.labelKey),
        color: metric.color,
      })),
    [t],
  );

  const series = useMemo(
    () => (granularity === "daily" ? dailyData ?? [] : monthlyData ?? []),
    [granularity, dailyData, monthlyData],
  );
  const totalPages = Math.max(1, Math.ceil(series.length / TREND_PAGE_SIZE));

  const pageData = useMemo(() => {
    const start = (page - 1) * TREND_PAGE_SIZE;
    return series.slice(start, start + TREND_PAGE_SIZE);
  }, [page, series]);

  const setGranularitySafe = useCallback((next: EngagementTrendGranularity) => {
    setGranularity(next);
    setPage(1);
  }, []);

  const toggleMetric = useCallback((key: string) => {
    setActiveMetrics(current => {
      const typedKey = key as TimeSeriesMetricKey;
      if (current.includes(typedKey)) {
        if (current.length === 1) {
          return current;
        }
        return current.filter(item => item !== typedKey);
      }
      return [...current, typedKey];
    });
  }, []);

  return (
    <article className="engagement-overview-panel engagement-overview-panel--trend">
      <div className="engagement-overview-panel__header engagement-overview-panel__header--trend">
        <div className="engagement-overview-panel__heading">
          <h3 className="engagement-overview-panel__heading-title">{resolvedSectionTitle}</h3>
          <DefinitionTooltip
            label={resolvedSectionTitle}
            info={getEngagementTooltipInfo(t, tooltipKey)}
          />
        </div>
        <div className="engagement-overview-panel__header-actions">
          <fieldset className="engagement-overview-granularity">
            <legend className="engagement-overview-visually-hidden">
              {t("engagementOverview.shared.timeSeriesGranularityLegend")}
            </legend>
            {(["daily", "monthly"] as const).map(option => (
              <button
                key={option}
                type="button"
                className={
                  granularity === option
                    ? "engagement-overview-granularity__btn engagement-overview-granularity__btn--active"
                    : "engagement-overview-granularity__btn"
                }
                onClick={() => setGranularitySafe(option)}
              >
                {option === "daily"
                  ? t("engagementOverview.shared.timeSeriesDaily")
                  : t("engagementOverview.shared.timeSeriesMonthly")}
              </button>
            ))}
          </fieldset>
        </div>
      </div>
      <ChartLegendChips
        items={legendItems}
        activeKeys={activeMetrics}
        onToggle={toggleMetric}
        ariaLabel={t("engagementOverview.shared.timeSeriesLegendAria")}
      />

      {!series.length ? (
        <EngagementEmptyState message={t("engagementOverview.shared.timeSeriesEmptyMessage")} />
      ) : (
        <>
          <div className="engagement-overview-chart engagement-overview-chart--trend">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={pageData}>
                <CartesianGrid stroke="#dce3ef" strokeDasharray="3 3" />
                <XAxis
                  dataKey="date"
                  tickFormatter={value => formatShortDate(String(value))}
                  tick={{ fontSize: 11, fill: "#60718b" }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  yAxisId="primary"
                  width={40}
                  tick={{ fontSize: 11, fill: "#60718b" }}
                  axisLine={false}
                  tickLine={false}
                  tickFormatter={value => formatAxisCompact(value)}
                />
                <YAxis
                  yAxisId="secondary"
                  orientation="right"
                  width={40}
                  tick={{ fontSize: 11, fill: "#60718b" }}
                  axisLine={false}
                  tickLine={false}
                  tickFormatter={value => formatPercent(Number(value ?? 0), 0)}
                />
                <Tooltip
                  content={({ active, label, payload }) => (
                    <OrganicChartTooltip
                      active={active}
                      label={formatTooltipDate(String(label ?? ""), i18n.language)}
                      labelTitle={t("engagementOverview.shared.timeSeriesDateLabel")}
                      payload={payload}
                      seriesLabelForDataKey={key => {
                        const metric = TIME_SERIES_METRICS.find(item => item.key === key);
                        return metric ? t(metric.labelKey) : key;
                      }}
                      valueFormatter={(value, name) => {
                        if (name === "openRate" || name === "bounceRate") {
                          return formatTooltipPercent(value, i18n.language);
                        }
                        return formatTooltipCount(normalizeTooltipValue(value), i18n.language);
                      }}
                    />
                  )}
                  wrapperStyle={{ pointerEvents: "none" }}
                />
                {TIME_SERIES_METRICS.filter(metric => activeMetrics.includes(metric.key)).map(
                  metric => (
                    <Line
                      key={metric.key}
                      type="monotone"
                      dataKey={metric.key}
                      name={metric.key}
                      yAxisId={metric.axis}
                      stroke={metric.color}
                      strokeWidth={2.4}
                      dot={false}
                      activeDot={{ r: 4 }}
                    />
                  ),
                )}
              </LineChart>
            </ResponsiveContainer>
          </div>
          <div className="engagement-overview-panel__pagination-right">
            <EngagementSectionPagination
              currentPage={page}
              totalPages={totalPages}
              onPrev={() => setPage(current => Math.max(1, current - 1))}
              onNext={() => setPage(current => Math.min(totalPages, current + 1))}
              ariaLabel={t("engagementOverview.shared.timeSeriesPaginationAria")}
            />
          </div>
        </>
      )}
    </article>
  );
};

export default TimeSeriesAnalysis;
